import { useState } from "react";
import { useListSedecUsers, useCreateSedecUser, useUpdateSedecUser, useSetSedecUserStatus, useListSedecEcoleOptions, getListSedecUsersQueryKey } from "@/lib/backoffice-hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useCurrentUser } from "@/lib/auth";
import { useDioceseScope } from "@/lib/diocese-scope";
import { usePaginationState } from "@/lib/use-paginated-query";
import { useDebouncedValue } from "@/lib/use-debounced-value";
import { PaginationControls } from "@/components/PaginationControls";
import SedecShell from "@/components/layout/SedecShell";
import StepUpReauthDialog from "@/components/StepUpReauthDialog";
import CsvImportCard from "@/components/CsvImportCard";
import { importUsersCsv } from "@/lib/api";
import { friendlyError } from "@/lib/errors";
import { masquerEmail } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, UserPlus, KeyRound, Power, PowerOff, Search, Pencil } from "lucide-react";
import { toast } from "sonner";

// Display labels (UPPERCASE keys are the prototype convention; RequireRole
// normalizes the user's effective role for guard checks).
const ROLE_LABEL: Record<string, string> = {
  ECOLE_ECONOME: "Économe école",
  ECOLE_CAISSE: "Agent caisse école",
  DIOCESE_ECONOMAT: "Économat diocésain",
  DIOCESE_AUDITEUR: "Auditeur diocésain",
  DIOCESE_SEDEC_ADMIN: "Admin SEDEC",
};
const ROLES = Object.keys(ROLE_LABEL);

// Le backend peut renvoyer le rôle sous sa forme canonique (econome, caisse, …)
// ou legacy (econome_etab) plutôt que le code UPPERCASE attendu par ROLE_LABEL.
// roleLabel() normalise d'abord vers le code puis fait le lookup.
const CANON_TO_CODE: Record<string, string> = {
  econome: "ECOLE_ECONOME", econome_etab: "ECOLE_ECONOME", caisse: "ECOLE_CAISSE",
  econome_diocesain: "DIOCESE_ECONOMAT", auditeur_diocesain: "DIOCESE_AUDITEUR", sedec_admin: "DIOCESE_SEDEC_ADMIN",
};
function roleLabel(role: string): string {
  const code = ROLE_LABEL[role] ? role : (CANON_TO_CODE[role] ?? role);
  return ROLE_LABEL[code] ?? role;
}

export default function SedecUsers() {
  const { page, size, setPage, setSize, resetToFirstPage } = usePaginationState(20);
  const { data: ecoles } = useListSedecEcoleOptions();
  const { user } = useCurrentUser();
  const { dioceses: myDioceses, dioceseId } = useDioceseScope();
  // Seul un admin global (admin/super_admin) peut créer un Admin SEDEC ;
  // un sedec_admin ne peut pas s'auto-cloner ce rôle privilégié.
  const isGlobalAdmin = (user?.roles ?? []).some((r) => r === "admin" || r === "super_admin");
  const availableRoles = isGlobalAdmin ? ROLES : ROLES.filter((r) => r !== "DIOCESE_SEDEC_ADMIN");
  const qc = useQueryClient();
  const createMut = useCreateSedecUser();
  const updateMut = useUpdateSedecUser();
  const statusMut = useSetSedecUserStatus();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ role: "ECOLE_ECONOME", email: "", password: "", nom: "", prenoms: "", phone: "", ecoleId: "", dioceseIds: [] as string[] });
  const [resetUser, setResetUser] = useState<{ id: string; label: string } | null>(null);
  const [newPwd, setNewPwd] = useState("");
  // Step-up : ré-authentification avant la création d'un compte (action sensible).
  const [stepUpOpen, setStepUpOpen] = useState(false);
  const [editUser, setEditUser] = useState<{ id: string; email: string; prenoms: string; nom: string; role: string } | null>(null);

  const [filterSearch, setFilterSearch] = useState("");
  const [filterRole, setFilterRole] = useState<string>("");
  const [filterEcoleId, setFilterEcoleId] = useState<string>("");
  const [filterActif, setFilterActif] = useState<"" | "actif" | "inactif">("");
  const debouncedSearch = useDebouncedValue(filterSearch, 300);

  const onSearchChange = (v: string) => { setFilterSearch(v); resetToFirstPage(); };
  const onRoleChange = (v: string) => { setFilterRole(v); resetToFirstPage(); };
  const onEcoleChange = (v: string) => { setFilterEcoleId(v); resetToFirstPage(); };
  const onActifChange = (v: "" | "actif" | "inactif") => { setFilterActif(v); resetToFirstPage(); };

  const { data, isLoading } = useListSedecUsers({
    page,
    size,
    search: debouncedSearch || undefined,
    role: filterRole || undefined,
    ecoleId: filterEcoleId || undefined,
    active: filterActif === "" ? undefined : filterActif === "actif",
  });
  const users = data?.items ?? [];
  const total = data?.total ?? 0;

  const onCreate = () => {
    if (!form.email || !form.password || !form.nom || !form.prenoms) {
      toast.error("Renseignez email, mot de passe, nom et prénoms");
      return;
    }
    const isEcole = form.role.startsWith("ECOLE_");
    const isDiocese = form.role.startsWith("DIOCESE_");
    if (isEcole && !form.ecoleId) { toast.error("Choisissez une école"); return; }
    if (isDiocese && form.dioceseIds.length === 0) { toast.error("Choisissez au moins un diocèse"); return; }
    // Création d'un compte back-office = action sensible → step-up (ré-auth).
    // On ferme d'abord le dialog de création (les données du formulaire restent
    // en state) pour ne pas empiler deux modales : le piège de focus Radix du
    // dialog empêcherait la saisie du mot de passe dans le step-up.
    setOpen(false);
    setStepUpOpen(true);
  };

  const runCreate = () => {
    const isEcole = form.role.startsWith("ECOLE_");
    const isDiocese = form.role.startsWith("DIOCESE_");
    createMut.mutate({ data: { ...form, ecoleId: isEcole ? form.ecoleId : undefined, dioceseIds: isDiocese ? form.dioceseIds : undefined, phone: form.phone || undefined } }, {
      onSuccess: () => {
        toast.success("Utilisateur créé");
        setOpen(false);
        setForm({ role: "ECOLE_ECONOME", email: "", password: "", nom: "", prenoms: "", phone: "", ecoleId: "", dioceseIds: [] });
        qc.invalidateQueries({ queryKey: getListSedecUsersQueryKey() });
      },
      onError: (e: unknown) => toast.error(friendlyError(e)),
    });
  };

  const toggleActif = (userId: string, actif: boolean) => {
    statusMut.mutate({ userId, active: !actif }, {
      onSuccess: () => {
        toast.success(actif ? "Utilisateur désactivé" : "Utilisateur réactivé");
        qc.invalidateQueries({ queryKey: getListSedecUsersQueryKey() });
      },
      onError: (e: unknown) => toast.error(friendlyError(e)),
    });
  };

  const submitResetPassword = () => {
    /* v8 ignore start -- défensif : submitResetPassword n'est appelable que depuis le dialog rendu quand resetUser est non nul (onKeyDown du champ / bouton submit) */
    if (!resetUser) return;
    /* v8 ignore stop */
    if (newPwd.length < 8) { toast.error("Minimum 8 caractères"); return; }
    updateMut.mutate({ userId: resetUser.id, data: { newPassword: newPwd } }, {
      onSuccess: () => {
        toast.success("Mot de passe réinitialisé");
        setResetUser(null);
        setNewPwd("");
      },
      onError: (e: unknown) => toast.error(friendlyError(e)),
    });
  };

  const submitEdit = () => {
    /* v8 ignore start -- défensif : submitEdit n'est appelable que depuis le dialog rendu quand editUser est non nul */
    if (!editUser) return;
    /* v8 ignore stop */
    updateMut.mutate(
      { userId: editUser.id, data: { role: editUser.role, prenoms: editUser.prenoms, nom: editUser.nom } },
      {
        onSuccess: () => {
          toast.success("Utilisateur modifié.");
          setEditUser(null);
        },
        onError: (e: unknown) => toast.error(friendlyError(e)),
      },
    );
  };

  return (
    <SedecShell>
      <div className="space-y-5">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Comptes back-office</div>
            <h1 className="text-2xl font-bold text-navy">{total} utilisateurs</h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-navy text-white hover:bg-navy/90" data-testid="button-new-user"><UserPlus className="w-4 h-4 mr-2" /> Nouveau compte</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader className="-mx-6 -mt-6 rounded-t-lg bg-navy px-6 py-4 text-left">
                <DialogTitle className="font-display text-lg font-bold text-white">Créer un compte back-office</DialogTitle>
                <DialogDescription className="text-white/70">Compte École, Économat ou SEDEC, rattaché à son périmètre.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Rôle</Label>
                  <select className="w-full mt-1 border border-slate-200 rounded h-9 px-2 text-sm" value={form.role} onChange={(e) => {
                    const role = e.target.value;
                    // Bascule vers un rôle diocésain → pré-cocher le diocèse actif.
                    const dioceseIds = role.startsWith("DIOCESE_") && dioceseId ? [dioceseId] : [];
                    setForm({ ...form, role, ecoleId: "", dioceseIds });
                  }} data-testid="select-role">
                    {availableRoles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                  </select>
                </div>
                {form.role.startsWith("ECOLE_") && (
                  <div>
                    <Label>École</Label>
                    <select className="w-full mt-1 border border-slate-200 rounded h-9 px-2 text-sm" value={form.ecoleId} onChange={(e) => setForm({ ...form, ecoleId: e.target.value })} data-testid="select-ecole">
                      <option value="">Choisir</option>
                      {ecoles?.map((e) => <option key={e.id} value={e.id}>{e.denomination}</option>)}
                    </select>
                  </div>
                )}
                {form.role.startsWith("DIOCESE_") && (
                  <div>
                    <Label>Diocèses rattachés</Label>
                    <select
                      multiple
                      className="w-full mt-1 border border-slate-200 rounded px-2 py-1 text-sm h-28"
                      value={form.dioceseIds}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          dioceseIds: Array.from(e.target.selectedOptions, (o) => o.value),
                        })
                      }
                      data-testid="select-dioceses"
                    >
                      {myDioceses.map((d) => (
                        <option key={d.id} value={d.id}>{d.nom}</option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-500 mt-1">Ctrl/Cmd+clic pour plusieurs.</p>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>Prénoms</Label><Input value={form.prenoms} onChange={(e) => setForm({ ...form, prenoms: e.target.value })} data-testid="input-prenoms" /></div>
                  <div><Label>Nom</Label><Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="input-nom" /></div>
                </div>
                <div><Label>Email</Label><Input type="email" autoComplete="off" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="input-email" /></div>
                <div><Label>Téléphone (optionnel)</Label><Input autoComplete="off" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>Mot de passe initial</Label><Input type="text" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="input-password" /></div>
                <Button onClick={onCreate} disabled={createMut.isPending} className="w-full bg-navy text-white hover:bg-navy/90">
                  {createMut.isPending ? "Création…" : "Créer le compte"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </header>

        <Card className="p-3 border border-slate-200 shadow-none">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input value={filterSearch} onChange={(e) => onSearchChange(e.target.value)} placeholder="Rechercher nom, email, école…" className="pl-8 h-8 text-sm" data-testid="input-search-users" type="search" name="recherche-comptes" autoComplete="off" data-lpignore="true" data-1p-ignore="true" />
            </div>
            <select value={filterRole} onChange={(e) => onRoleChange(e.target.value)} className="border border-slate-200 rounded h-8 px-2 text-xs" data-testid="filter-role">
              <option value="">Tous rôles</option>
              {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            </select>
            <select value={filterEcoleId} onChange={(e) => onEcoleChange(e.target.value)} className="border border-slate-200 rounded h-8 px-2 text-xs max-w-[200px]" data-testid="filter-ecole">
              <option value="">Toutes écoles</option>
              {ecoles?.map((e) => <option key={e.id} value={e.id}>{e.denomination}</option>)}
            </select>
            <div className="flex gap-1">
              {([["", "Tous"], ["actif", "Actifs"], ["inactif", "Désactivés"]] as const).map(([v, l]) => (
                <button key={v || "all"} onClick={() => onActifChange(v)} className={`text-xs px-2.5 py-1 rounded border ${filterActif === v ? "bg-navy text-white border-navy" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`} data-testid={`filter-actif-${v || "all"}`}>{l}</button>
              ))}
            </div>
            <div className="text-xs text-slate-500 ml-auto">{users.length} / {total}</div>
          </div>
        </Card>

        <Card className="p-0 border border-slate-200 shadow-none overflow-hidden">
          {isLoading && <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>}
          {!isLoading && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200 bg-slate-50">
                  <tr>
                    <th className="text-left px-4 py-2.5">Identité</th>
                    <th className="text-left">Email</th>
                    <th className="text-left">Rôle</th>
                    <th className="text-left">École</th>
                    <th className="text-left">Dernière connexion</th>
                    <th className="text-center">Statut</th>
                    <th className="text-right pr-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!users.length && <tr><td colSpan={7} className="py-8 text-center text-slate-500">Aucun utilisateur ne correspond aux filtres.</td></tr>}
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`row-user-${u.id}`}>
                      <td className="px-4 py-2 font-medium text-navy">{u.prenoms} {u.nom}</td>
                      <td className="text-slate-700" title="Adresse complète : bouton Modifier">{masquerEmail(u.email)}</td>
                      <td className="text-xs"><span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">{roleLabel(u.role)}</span></td>
                      <td className="text-slate-600 text-xs truncate max-w-[180px]">{u.ecoleDenomination ?? "·"}</td>
                      <td className="text-xs text-slate-500">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString("fr-FR") : "Jamais"}</td>
                      <td className="text-center"><span className={`text-xs px-2 py-0.5 rounded ${u.actif ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{u.actif ? "Actif" : "Désactivé"}</span></td>
                      <td className="text-right pr-4 space-x-2 whitespace-nowrap">
                        <button onClick={() => setEditUser({ id: u.id, email: u.email, prenoms: u.prenoms, nom: u.nom, role: CANON_TO_CODE[u.role] ?? u.role })} className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-1" data-testid={`button-edit-${u.id}`}><Pencil className="w-3 h-3" /> Modifier</button>
                        <button onClick={() => { setResetUser({ id: u.id, label: `${u.prenoms} ${u.nom}` }); setNewPwd(""); }} className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-1" data-testid={`button-reset-${u.id}`}><KeyRound className="w-3 h-3" /> Mot de passe</button>
                        <button onClick={() => toggleActif(u.id, u.actif)} className={`text-xs px-2 py-1 border rounded inline-flex items-center gap-1 ${u.actif ? "border-red-200 text-red-700 hover:bg-red-50" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`} data-testid={`button-toggle-${u.id}`}>
                          {u.actif ? <><PowerOff className="w-3 h-3" /> Désactiver</> : <><Power className="w-3 h-3" /> Activer</>}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <PaginationControls page={page} size={size} total={total} onPageChange={setPage} onSizeChange={setSize} />
            </div>
          )}
        </Card>
      </div>

      <Dialog
        open={!!resetUser}
        onOpenChange={(o) => {
          /* v8 ignore start -- défensif : sans DialogTrigger, Radix n'appelle onOpenChange qu'avec o=false (fermeture) ; la branche o=true est inatteignable */
          if (!o) {
            setResetUser(null);
            setNewPwd("");
          }
          /* v8 ignore stop */
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader className="-mx-6 -mt-6 rounded-t-lg bg-navy px-6 py-4 text-left">
            <DialogTitle className="font-display text-lg font-bold text-white">Réinitialiser le mot de passe</DialogTitle>
            <DialogDescription className="text-white/70">Génère un nouveau mot de passe pour ce compte.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Pour le compte <span className="font-semibold text-navy">{resetUser?.label}</span>. L'utilisateur devra se reconnecter avec ce nouveau mot de passe.</p>
            <div>
              <Label>Nouveau mot de passe (min 8 caractères)</Label>
              <Input
                type="text"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") submitResetPassword(); }}
                data-testid="input-new-password"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setResetUser(null); setNewPwd(""); }}>Annuler</Button>
              <Button onClick={submitResetPassword} disabled={newPwd.length < 8 || updateMut.isPending} className="bg-navy text-white hover:bg-navy/90" data-testid="button-submit-reset">
                {updateMut.isPending ? "Application…" : "Réinitialiser"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!editUser}
        onOpenChange={(o) => {
          /* v8 ignore start -- défensif : sans DialogTrigger, Radix n'appelle onOpenChange qu'avec o=false (fermeture) ; la branche o=true est inatteignable */
          if (!o) setEditUser(null);
          /* v8 ignore stop */
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader className="-mx-6 -mt-6 rounded-t-lg bg-navy px-6 py-4 text-left">
            <DialogTitle className="font-display text-lg font-bold text-white">Modifier l'utilisateur</DialogTitle>
            <DialogDescription className="text-white/70">Modifiez le rôle et le nom. L'email n'est pas modifiable.</DialogDescription>
          </DialogHeader>
          {/* Radix monte DialogContent même fermé (jsdom) → on lit editUser de
              façon défensive ; les onChange ne tirent que dialog ouvert (editUser non nul). */}
          <div className="space-y-3">
            <div>
              <Label>Email</Label>
              <Input value={editUser?.email ?? ""} disabled data-testid="edit-email-readonly" />
            </div>
            <div>
              <Label>Rôle</Label>
              <select className="w-full mt-1 border border-slate-200 rounded h-9 px-2 text-sm" value={editUser?.role ?? ""} onChange={(e) => setEditUser((p) => p && { ...p, role: e.target.value })} data-testid="edit-select-role">
                {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Prénoms</Label><Input value={editUser?.prenoms ?? ""} onChange={(e) => setEditUser((p) => p && { ...p, prenoms: e.target.value })} data-testid="edit-input-prenoms" /></div>
              <div><Label>Nom</Label><Input value={editUser?.nom ?? ""} onChange={(e) => setEditUser((p) => p && { ...p, nom: e.target.value })} data-testid="edit-input-nom" /></div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setEditUser(null)}>Annuler</Button>
              <Button onClick={submitEdit} disabled={updateMut.isPending} className="bg-navy text-white hover:bg-navy/90" data-testid="button-submit-edit">
                {updateMut.isPending ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <CsvImportCard
        title="Importer des utilisateurs"
        description="Fichier CSV avec en-tête: email,nom,prenoms,role,ecoleCode. Chaque compte reçoit un mot de passe provisoire, à changer à la première connexion."
        columns={["email", "nom", "prenoms", "role", "ecoleCode"]}
        exampleCsv={`email,nom,prenoms,role,ecoleCode
econome.stjoseph@ecole.ci,KONE,Adama,econome,ST-JO001
caisse.sainte-marie@ecole.ci,YAO,Marie,caisse,STE-MARIE`}
        importFn={importUsersCsv}
        filename="modele-utilisateurs.csv"
        invalidateQueries={[getListSedecUsersQueryKey()]}
      />

      <StepUpReauthDialog
        open={stepUpOpen}
        onOpenChange={setStepUpOpen}
        email={user?.email}
        onVerified={runCreate}
        title="Confirmez votre identité"
        description="La création d'un compte back-office nécessite de saisir à nouveau votre mot de passe."
      />
    </SedecShell>
  );
}
